import type { Appearance, ShaftFinish } from './RealisticArrow';

/**
 * Cosmetic appearance picker for Render mode — recolor the cock/hen vanes,
 * nock, and wrap, and pick a shaft finish. Pure UI over the `Appearance` value;
 * nothing here touches ballistics. Inline styles keep the swatches consistent
 * across pages regardless of the host stylesheet.
 */
const PALETTE = [
  '#f59e0b', // ember
  '#38bdf8', // sky
  '#ef4444', // red
  '#22c55e', // green
  '#a855f7', // violet
  '#ec4899', // pink
  '#eab308', // yellow
  '#f97316', // orange
  '#0ea5e9', // blue
  '#e4e4e7', // white
  '#18181b', // black
];

const SHAFT_OPTS: { key: ShaftFinish; label: string }[] = [
  { key: 'carbon', label: 'Carbon' },
  { key: 'black', label: 'Black' },
  { key: 'white', label: 'White' },
  { key: 'olive', label: 'Olive' },
];

interface Props {
  value: Appearance;
  onChange: (a: Appearance) => void;
}

export function AppearancePicker({ value, onChange }: Props) {
  const set = (patch: Partial<Appearance>) => onChange({ ...value, ...patch });
  return (
    <div className="mt-3 grid gap-2.5">
      <SwatchRow label="Cock vane" selected={value.cockVaneColor} onPick={(c) => set({ cockVaneColor: c })} />
      <SwatchRow label="Hen vanes" selected={value.vaneColor} onPick={(c) => set({ vaneColor: c })} />
      <SwatchRow label="Nock" selected={value.nockColor} onPick={(c) => set({ nockColor: c })} />
      <SwatchRow label="Wrap" selected={value.wrapColor} onPick={(c) => set({ wrapColor: c })} />
      <div className="flex items-center gap-2">
        <span className="w-20 shrink-0 text-[10px] font-mono uppercase tracking-wider text-zinc-500">Shaft</span>
        <div className="flex gap-1.5 flex-wrap">
          {SHAFT_OPTS.map((o) => {
            const on = value.shaftFinish === o.key;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => set({ shaftFinish: o.key })}
                aria-pressed={on}
                style={{
                  background: on ? '#f59e0b' : 'transparent',
                  color: on ? '#18181b' : '#a1a1aa',
                  border: '1px solid #3f3f46',
                  borderRadius: 4,
                  padding: '3px 9px',
                  fontSize: 11,
                  fontWeight: on ? 600 : 400,
                  cursor: 'pointer',
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SwatchRow({
  label,
  selected,
  onPick,
}: {
  label: string;
  selected: string;
  onPick: (c: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-20 shrink-0 text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</span>
      <div className="flex gap-1.5 flex-wrap">
        {PALETTE.map((c) => {
          const on = selected.toLowerCase() === c.toLowerCase();
          return (
            <button
              key={c}
              type="button"
              aria-label={`${label}: ${c}`}
              onClick={() => onPick(c)}
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: c,
                cursor: 'pointer',
                border: on ? '2px solid #f59e0b' : '1px solid #52525b',
                boxShadow: on ? '0 0 0 1px #18181b' : 'none',
                padding: 0,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
